import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { HERO_STATS } from "../data/content";
import { usePortfolio } from "../lib/usePortfolio";
import { scrollToSection } from "../lib/lenisStore";

const EASE = [0.22, 1, 0.36, 1];

const LINES = [
  { text: "The Art of", italic: false },
  { text: "Unscripted", italic: true },
  { text: "Romance", italic: false },
];

export default function Hero() {
  const { hero: HERO_IMAGE } = usePortfolio();
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 50, damping: 18 });
  const sy = useSpring(my, { stiffness: 50, damping: 18 });
  const rotateX = useTransform(sy, [0, 1], [3.5, -3.5]);
  const rotateY = useTransform(sx, [0, 1], [-3.5, 3.5]);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section
      id="hero"
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden"
    >
      <motion.div
        style={{ rotateX, rotateY, transformPerspective: 1400 }}
        className="absolute inset-4 md:inset-8 will-change-transform"
      >
        <motion.div style={{ y: imgY }} className="relative h-[110%] w-full overflow-hidden">
          <motion.img
            initial={{ scale: 1.15, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 2, ease: EASE }}
            src={HERO_IMAGE.url}
            alt={HERO_IMAGE.alt}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/35 to-obsidian/20" />
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pb-16 md:pb-24"
      >
        <motion.p
          data-testid="hero-badge"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.9, ease: EASE }}
          className="text-[11px] font-mono tracking-[0.3em] uppercase text-champagne/80 mb-6"
        >
          Documenting Love Across Continents Since 2014
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-ivory"
        >
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.2, delay: 0.5 + i * 0.16, ease: EASE }}
                className={`block ${line.italic ? "italic text-champagne" : ""}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 1, ease: EASE }}
          className="mt-6 max-w-md text-sm sm:text-base font-light leading-relaxed text-stone-300"
        >
          We do not pose life. We capture the unscripted poetry of your union.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 1, ease: EASE }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-inquire-btn"
            onClick={() => scrollToSection("contact")}
            className="bg-champagne text-obsidian px-7 py-3 text-[11px] font-mono tracking-[0.2em] uppercase hover:bg-ivory transition-colors duration-300"
          >
            Reserve a Date
          </button>
          <button
            data-testid="hero-archive-btn"
            onClick={() => scrollToSection("gallery")}
            className="border border-white/20 px-7 py-3 text-[11px] font-mono tracking-[0.2em] uppercase text-stone-200 hover:border-champagne hover:text-champagne transition-colors duration-300"
          >
            View the Archive
          </button>
        </motion.div>

        <motion.div
          data-testid="hero-stats"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1.2 }}
          className="mt-12 flex flex-wrap gap-x-8 gap-y-2 border-t border-white/10 pt-5"
        >
          {HERO_STATS.map((stat) => (
            <span key={stat} className="text-[11px] font-mono tracking-[0.2em] uppercase text-stone-400">
              {stat}
            </span>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
